import { useEffect, useRef, useState } from "react";
import { Mic, Square, Play, Trash2 } from "lucide-react";
import { isAudioRecordingSupported, startAudioRecording, playAudioDataUrl } from "@/lib/media";
import FirstUseConsent, { useFirstUseConsent } from "./FirstUseConsent";

const MAX_SECONDS = 8;

export default function AudioRecorder({ value, onChange, profileId }) {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState("");
  const [playing, setPlaying] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  const sessionRef = useRef(null);
  const timerRef = useRef(null);

  const consent = useFirstUseConsent({
    feature: "voice",
    profileId,
    enabled: !!profileId,
  });

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (sessionRef.current) sessionRef.current.cancel();
  }, []);

  const supported = isAudioRecordingSupported();

  const start = async () => {
    setError("");
    try {
      const sess = await startAudioRecording();
      sessionRef.current = sess;
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed((e) => {
          if (e + 1 >= MAX_SECONDS) {
            stop(); // auto-stop
          }
          return e + 1;
        });
      }, 1000);
    } catch (e) {
      setError(
        e?.name === "NotAllowedError"
          ? "Microphone access was blocked. Please allow it in your browser."
          : e?.message || "Could not start recording"
      );
      setRecording(false);
    }
  };

  const stop = async () => {
    if (!sessionRef.current) return;
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    const dataUrl = await sessionRef.current.stop();
    sessionRef.current = null;
    setRecording(false);
    setElapsed(0);
    if (dataUrl) onChange(dataUrl);
  };

  const preview = async () => {
    if (!value || playing) return;
    setPlaying(true);
    await playAudioDataUrl(value);
    setPlaying(false);
  };

  const clear = () => {
    onChange(null);
  };

  if (!supported) {
    return (
      <div className="brutal-card p-3 text-sm text-[color:var(--ink-soft)]" data-testid="audio-recorder-unsupported">
        Audio recording is not supported in this browser.
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="audio-recorder">
      <FirstUseConsent
        feature="voice"
        profileId={profileId}
        open={showConsent && consent.status === "needed"}
        onAccept={() => {
          consent.markAccepted();
          setShowConsent(false);
          start();
        }}
        onCancel={() => setShowConsent(false)}
      />
      <div className="flex items-center gap-2 flex-wrap">
        {!recording ? (
          <button
            type="button"
            className="pill-btn"
            onClick={() => {
              if (consent.status === "needed") setShowConsent(true);
              else start();
            }}
            disabled={consent.status === "loading"}
            data-testid="audio-record-start"
          >
            <Mic size={16} />
            <span>{value ? "Re-record" : "Record voice"}</span>
          </button>
        ) : (
          <button
            type="button"
            className="pill-btn primary"
            onClick={stop}
            data-testid="audio-record-stop"
          >
            <Square size={14} />
            <span>Stop ({MAX_SECONDS - elapsed}s)</span>
          </button>
        )}

        {value && !recording && (
          <>
            <button type="button" className="pill-btn gold" onClick={preview} data-testid="audio-preview">
              <Play size={14} /> {playing ? "Playing…" : "Preview"}
            </button>
            <button type="button" className="pill-btn danger" onClick={clear} data-testid="audio-clear">
              <Trash2 size={14} /> Remove
            </button>
          </>
        )}
      </div>
      {recording && (
        <div className="text-sm text-[color:var(--coral-deep)]" aria-live="polite">
          Recording… speak now ({MAX_SECONDS - elapsed}s left)
        </div>
      )}
      {value && !recording && (
        <div className="text-sm text-[color:var(--ink-soft)]">
          Custom voice saved — this will play instead of the built-in voice.
        </div>
      )}
      {error && <div className="text-sm text-[color:var(--coral-deep)]" data-testid="audio-error">{error}</div>}
    </div>
  );
}
